import { apiAdminRoutes } from 'lib';
import { apiClient } from 'lib/ApiClient';
import {
  IAdminFetchUsersResponse,
  IPaymentsCalculateResponse,
  IServerDateTimeResponse,
  TPaymentsCalculateQuery,
  ICostsResponse,
  INewbieAdjustResponse,
  INewbieCostResponse,
  IYesterdayCostResponse,
  IUserCostResponse,
  IUserAdjustResponse,
  IUserVipResponse,
  IUserBanResponse,
  IAdminExportUsersResponse,
  IAdminFetchPaymentHistoryResponse,
  IAdminExportPaymentHistoryResponse,
  TAdminPaymentHistoryQuery,
  TAdminUsersQuery,
  IAdminFetchDeviceGroupsResponse,
  TAdminDeviceGroupsQuery,
  IAdminExportDeviceGroupsResponse,
  IDeviceGroupAdjustResponse,
  TAdminEquipmentGroupsQuery,
  IAdminFetchEquipmentGroupsResponse,
  IAdminExportEquipmentGroupsResponse,
  IEquipmentGroupDeviceGroupNameResponse,
  TAdminPoolConfigsQuery,
  IAdminFetchPoolConfigsResponse,
  TAdminGroupPoolConfigsQuery,
  IAdminFetchGroupPoolConfigsResponse,
  IAdminDeletePoolConfigResponse,
  IAdminAddPoolConfigResponse,
  IAdminAddPoolConfigData,
  IAdminUpdatePoolConfigResponse,
  IAdminUpdatePoolConfigData,
} from 'types';

export const AdminRepository = {
  getPaymentsCalculate: async (token: string, params: TPaymentsCalculateQuery) => {
    const url = apiAdminRoutes.apiAdminPaymentsCalculate();
    const response = apiClient.post(
      `${url}`,
      {},
      {
        queryParams: {
          date: params.date,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response as Promise<IPaymentsCalculateResponse>;
  },
  getServerDateTime: async (token: string) => {
    const url = apiAdminRoutes.apiFetchServerDateTime();
    const response = apiClient.get(`${url}`, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IServerDateTimeResponse>;
  },
  getCosts: async (token: string) => {
    const url = apiAdminRoutes.apiAdminCosts();
    const response = apiClient.get(`${url}`, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<ICostsResponse>;
  },
  setYesterdayCost: async (
    token: string,
    costData: {
      yesterdayCost: number;
      newbieCost: number;
      newbieAdjust: number;
    },
  ) => {
    const url = apiAdminRoutes.apiAdminYesterdayCost();
    const editYesterdayCostResponse = (await apiClient.post(url, costData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })) as IYesterdayCostResponse;
    return editYesterdayCostResponse;
  },
  setNewbieCost: async (token: string, costData: { newbieCost: number }) => {
    const url = apiAdminRoutes.apiAdminNewbieCost();
    const editNewbieCostResponse = (await apiClient.post(url, costData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })) as INewbieCostResponse;
    return editNewbieCostResponse;
  },
  setNewbieAdjust: async (
    token: string,
    adjustData: {
      newbieAdjust: number;
    },
  ) => {
    const url = apiAdminRoutes.apiAdminNewbieAdjust();
    const editNewbieAdjustResponse = (await apiClient.post(url, adjustData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })) as INewbieAdjustResponse;
    return editNewbieAdjustResponse;
  },
  getUsers: async (token: string, params: TAdminUsersQuery) => {
    const url = apiAdminRoutes.apiAdminFetchUsers();
    const queryParams = new URLSearchParams(params);
    const response = apiClient.get(`${url}?${queryParams.toString()}`, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IAdminFetchUsersResponse>;
  },
  setUserCost: async (
    token: string,
    costData: {
      userId: number;
      cost: number;
    },
  ) => {
    const url = apiAdminRoutes.apiAdminUserCost();
    const editUserCostResponse = (await apiClient.post(url, costData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })) as IUserCostResponse;
    return editUserCostResponse;
  },
  setUserAdjust: async (
    token: string,
    adjustData: {
      userId: number;
      adjust: number;
    },
  ) => {
    const url = apiAdminRoutes.apiAdminUserAdjust();
    const editUserAdjustResponse = (await apiClient.post(url, adjustData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })) as IUserAdjustResponse;
    return editUserAdjustResponse;
  },
  setUserVipStatus: async (
    token: string,
    vipData: {
      userId: number;
      vip: boolean;
    },
  ) => {
    const url = apiAdminRoutes.apiAdminUserVip();
    const editUserVipResponse = (await apiClient.post(url, vipData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })) as IUserVipResponse;
    return editUserVipResponse;
  },
  setUserBanStatus: async (
    token: string,
    banData: {
      userId: number;
    },
  ) => {
    const url = apiAdminRoutes.apiAdminUserBan();
    const editUserBanResponse = (await apiClient.post(url, banData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })) as IUserBanResponse;
    return editUserBanResponse;
  },
  getUsersExport: async (token: string) => {
    const url = apiAdminRoutes.apiAdminUsersExport();
    const response = apiClient.get(url, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IAdminExportUsersResponse>;
  },
  getPaymentHistory: async (token: string, params: TAdminPaymentHistoryQuery) => {
    const url = apiAdminRoutes.apiAdminFetchPaymentHistory();
    const queryParams = new URLSearchParams(params);
    const response = apiClient.get(`${url}?${queryParams.toString()}`, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IAdminFetchPaymentHistoryResponse>;
  },
  getPaymentHistoryExport: async (token: string) => {
    const url = apiAdminRoutes.apiAdminPaymentHistoryExport();
    const response = apiClient.get(url, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IAdminExportPaymentHistoryResponse>;
  },
  getDeviceGroups: async (token: string, params: TAdminDeviceGroupsQuery) => {
    const url = apiAdminRoutes.apiAdminFetchDeviceGroups();
    const queryParams = new URLSearchParams(params);
    const response = apiClient.get(`${url}?${queryParams.toString()}`, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IAdminFetchDeviceGroupsResponse>;
  },
  setDeviceGroupAdjust: async (
    token: string,
    adjustData: {
      groupId: number;
      adjust: number;
    },
  ) => {
    const url = apiAdminRoutes.apiAdminDeviceGroupAdjust();
    const editDeviceGroupAdjustResponse = (await apiClient.post(url, adjustData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })) as IDeviceGroupAdjustResponse;
    return editDeviceGroupAdjustResponse;
  },
  getDeviceGroupsExport: async (token: string) => {
    const url = apiAdminRoutes.apiAdminDeviceGroupsExport();
    const response = apiClient.get(url, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IAdminExportDeviceGroupsResponse>;
  },
  getEquipmentGroups: async (token: string, params: TAdminEquipmentGroupsQuery) => {
    const url = apiAdminRoutes.apiAdminFetchEquipmentGroups();
    const queryParams = new URLSearchParams(params);
    const response = apiClient.get(`${url}?${queryParams.toString()}`, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IAdminFetchEquipmentGroupsResponse>;
  },
  getEquipmentGroupsExport: async (token: string) => {
    const url = apiAdminRoutes.apiAdminEquipmentGroupsExport();
    const response = apiClient.get(url, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IAdminExportEquipmentGroupsResponse>;
  },
  setEquipmentGroupDeviceGroupName: async (
    token: string,
    groupData: {
      workerId: number;
      groupId: number;
    },
  ) => {
    const url = apiAdminRoutes.apiAdminEquipmentGroupDeviceGroupName();
    const editEquipmentGroupDeviceGroupNameResponse = (await apiClient.post(url, groupData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })) as IEquipmentGroupDeviceGroupNameResponse;
    return editEquipmentGroupDeviceGroupNameResponse;
  },
  getPoolConfigs: async (token: string, params: TAdminPoolConfigsQuery) => {
    const url = apiAdminRoutes.apiAdminPoolConfigs();
    const queryParams = new URLSearchParams(params);
    const response = apiClient.get(`${url}?${queryParams.toString()}`, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IAdminFetchPoolConfigsResponse>;
  },
  addPoolConfig: async (token: string, addPoolConfigData: IAdminAddPoolConfigData) => {
    const url = apiAdminRoutes.apiAdminPoolConfigs();
    const addPoolConfigResponse = (await apiClient.post(url, addPoolConfigData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })) as IAdminAddPoolConfigResponse;
    return addPoolConfigResponse;
  },
  deletePoolConfig: async (token: string, poolId: number) => {
    const url = apiAdminRoutes.apiAdminPoolConfigs();
    const deletePoolConfigResponse = (await apiClient.delete(`${url}/${poolId.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })) as IAdminDeletePoolConfigResponse;
    return deletePoolConfigResponse;
  },
  deletePoolConfigFromGroup: async (token: string, poolId: number, groupId: number) => {
    const url = apiAdminRoutes.apiAdminGroupPoolConfigs();
    const deletePoolConfigResponse = (await apiClient.delete(`${url}/${poolId.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      queryParams: {
        groupId: groupId.toString(),
      }
    })) as IAdminDeletePoolConfigResponse;
    return deletePoolConfigResponse;
  },
  setPoolConfig: async (token: string, updatePoolConfigData: IAdminUpdatePoolConfigData) => {
    const url = apiAdminRoutes.apiAdminPoolConfigs();
    const editPoolConfigResponse = (await apiClient.put(url, updatePoolConfigData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })) as IAdminUpdatePoolConfigResponse;
    return editPoolConfigResponse;
  },
  getGroupPoolConfigs: async (
    token: string,
    deviceGroupId: number,
    params: TAdminGroupPoolConfigsQuery,
  ) => {
    const url = apiAdminRoutes.apiAdminGroupPoolConfigs();
    const queryParams = new URLSearchParams(params);
    const response = apiClient.get(`${url}/${deviceGroupId.toString()}?${queryParams.toString()}`, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IAdminFetchGroupPoolConfigsResponse>;
  },
  getAllPoolConfigs: async (
      token: string,
  ) => {
    const url = apiAdminRoutes.apiAdminAllPoolConfigs();
    const response = apiClient.get(`${url}`, {
      Authorization: `Bearer ${token}`,
    });
    return response as Promise<IAdminFetchGroupPoolConfigsResponse>;
  },
};
